<template>
    <div class="w-full h-full flex flex-col justify-center py-12">
        <div class="relative sm:pt-32 sm:mx-auto sm:w-full sm:max-w-md">
            <img
                class="mx-auto h-12 w-auto"
                src="@/assets/logo-full.png"
                alt="upyield logo"
            >
            <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
                Sign in to your account
            </h2>
        </div>

        <div
            v-loading="isLoading"
            class="relative mt-8 mx-4 sm:mx-auto sm:w-full sm:max-w-xl"
        >
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <form @submit.prevent="login">
                    <div>
                        <label
                            for="username"
                            class="block text-sm font-medium leading-5 text-gray-700"
                        >
                            Email address
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <input
                                id="username"
                                v-model="username"
                                type="email"
                                required
                                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                            >
                        </div>
                    </div>

                    <div class="mt-6">
                        <label
                            for="password"
                            class="block text-sm font-medium leading-5 text-gray-700"
                        >
                            Password
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <input
                                id="password"
                                v-model="password"
                                type="password"
                                required
                                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                            >
                        </div>
                    </div>

                    <div class="mt-6">
                        <span class="block w-full rounded-md shadow-sm">
                            <button
                                type="submit"
                                class="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-700 hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:shadow-outline-primary active:bg-primary-700 transition duration-150 ease-in-out"
                                @click="login"
                            >
                                Sign in
                            </button>
                        </span>
                    </div>
                </form>

                <div class="mt-6">
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300" />
                        </div>
                        <div class="relative flex justify-center text-sm leading-5">
                            <span class="px-2 bg-white text-gray-500">
                                or
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="isGoogleOAuthEnabled"
                        class="mt-6"
                    >
                        <button
                            type="button"
                            :class="[
                                'w-full inline-flex justify-center items-center py-2 px-4',
                                'border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-700',
                                'hover:bg-gray-50 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out',
                            ]"
                            @click="loginWithGoogle"
                        >
                            <svg
                                class="w-5 h-5 mr-2"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    fill="#4285F4"
                                />
                                <path
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    fill="#34A853"
                                />
                                <path
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                                    fill="#FBBC05"
                                />
                                <path
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    fill="#EA4335"
                                />
                            </svg>
                            Sign in with Google
                        </button>
                    </div>

                    <div class="mt-4 grid grid-cols-2 gap-4">
                        <div>
                            <span class="w-full inline-flex rounded-md shadow-sm">
                                <router-link
                                    :to="{ name: 'Register' }"
                                    :class="[
                                        'w-full inline-flex justify-center py-2 px-4',
                                        'border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500',
                                        'hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out',
                                    ]"
                                >
                                    Register
                                </router-link>
                            </span>
                        </div>

                        <div>
                            <span class="w-full inline-flex rounded-md shadow-sm">
                                <router-link
                                    :to="{ name: 'ForgotPassword' }"
                                    :class="[
                                        'w-full inline-flex justify-center py-2 px-4',
                                        'border border-gray-300 rounded-md bg-white text-sm leading-5 font-medium text-gray-500',
                                        'hover:text-gray-400 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition duration-150 ease-in-out',
                                    ]"
                                >
                                    Forgot password
                                </router-link>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { login as authLogin, loginWithGoogle as authLoginWithGoogle } from '@/store/auth';
import config from '@/config';

import router from '@/router';

export default defineComponent({
    name: 'Login',
    setup () {
        const username = ref('');
        const password = ref('');
        const isLoading = ref(false);

        function login () {
            isLoading.value = true;
            authLogin({ username: username.value, password: password.value })
                .then(() => router.push({ name: 'Dashboard' }))
                .finally(() => isLoading.value = false);
        }

        function loginWithGoogle () {
            isLoading.value = true;
            authLoginWithGoogle()
                .finally(() => isLoading.value = false);
        }

        const isGoogleOAuthEnabled = computed(() => config.value.feature?.google_oauth?.enabled);

        return {
            login,
            loginWithGoogle,
            isGoogleOAuthEnabled,
            username,
            password,
            isLoading,
        };
    },
});
</script>

<style lang="scss">
.Login .navigation {
    display: none;
}
</style>
